import { CSSProperties } from 'react';
import Router from 'next/router';
import api from 'api';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { ConfirmButton } from 'components/common';
import { ReaderCompletionNotification } from '.';

import type { ReaderSettings } from 'shared-types';
import type { ButtonSize } from 'components/common/Button';

const ReaderRecomplete = ({ style }: { style?: CSSProperties }) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: {
      chapterSlug,
      completion,
      presentMode,
      readOnly,
      readerCourseData: { course, toc },
      settings,
      settingsPresentMode
    }
  } = useReduxState();

  const handleRecomplete = async () => {
    const response = await api.post(
      `/reader/courses/${course.slug}/completion-inactivate`
    );
    if (response.data && response.data.success) {
      if (toc.chapters[toc.chaptersOrder[0]].slug === chapterSlug) {
        dispatch.updatePageState({
          chapterForceReload: true,
          completion: null,
          isCompleted: false,
          showUncompletedCourseWarning: false,
          taskBlockIdsWithUnsavedAnswers: {}
        });
      } else {
        dispatch.updatePageState({
          completion: null,
          isCompleted: false,
          showUncompletedCourseWarning: false,
          taskBlockIdsWithUnsavedAnswers: {}
        });
        Router.push(
          '/courses/[...courseParams]',
          `/courses/${course.slug}/${toc.chapters[toc.chaptersOrder[0]].slug}`
        );
      }
      return true;
    }
    return false;
  };

  if (readOnly || !completion || !completion.expirationDate) {
    return null;
  }

  const currentSettings =
    (presentMode ? settingsPresentMode : settings) || ({} as ReaderSettings);

  return (
    <ReaderCompletionNotification
      description={
        (Date.parse(completion.expirationDate as string) < dateNow
          ? messages.readerRecompleteInfoExpired
          : messages.readerRecompleteInfoWillExpire) +
        ` ${getFormattedDate({ date: completion.expirationDate })}.`
      }
      icon="info-circle"
      style={style}
    >
      <ConfirmButton
        actionText={messages.readerRecompleteAction}
        buttonSize={
          {
            small: 'small',
            medium: 'small',
            large: 'medium',
            huge: 'medium'
          }[currentSettings.fontSize] as ButtonSize
        }
        buttonText={messages.readerRecompleteAction}
        confirmText={messages.readerRecompleteWarning}
        confirmTitle={messages.readerRecompleteTitle}
        onConfirm={handleRecomplete}
      />
    </ReaderCompletionNotification>
  );
};

export default ReaderRecomplete;
